import React, { useEffect, useState } from 'react';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col, Badge, Spinner, Alert
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';

const emptyForm = {
  tb_studyyear_year_id: '',
  semester: '1',
  term_name: '',
  due_date: '',
  term_status: 'open'
};

const FeeTerms = () => {
  const navigate = useNavigate();
  const [terms, setTerms] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [termRes, refRes] = await Promise.all([
        axiosInstance.get('/api/fee/terms'),
        axiosInstance.get('/api/fee/references')
      ]);
      if (termRes.data.success) setTerms(termRes.data.data);
      if (refRes.data.success) setYears(refRes.data.data.years);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลงวดไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, tb_studyyear_year_id: years[0]?.year_id || '' });
    setShowModal(true);
  };

  const openEdit = (term) => {
    setEditing(term);
    setForm({
      tb_studyyear_year_id: term.tb_studyyear_year_id,
      semester: term.semester,
      term_name: term.term_name || '',
      due_date: term.due_date ? term.due_date.substring(0, 10) : '',
      term_status: term.term_status
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.tb_studyyear_year_id || !form.semester) {
      setError('กรุณาเลือกปีการศึกษาและภาคเรียน');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, due_date: form.due_date || null };
      if (editing) {
        await axiosInstance.put(`/api/fee/terms/${editing.term_id}`, {
          term_name: payload.term_name,
          due_date: payload.due_date,
          term_status: payload.term_status
        });
      } else {
        await axiosInstance.post('/api/fee/terms', payload);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="mb-1">งวด / ภาคเรียน</h2>
              <p className="text-muted mb-0">งวดเรียกเก็บค่าใช้จ่าย อิงปีการศึกษาและภาคเรียน</p>
            </div>
            <Button variant="primary" onClick={openCreate}>
              <i className="ri-add-line me-1" /> เปิดงวดใหม่
            </Button>
          </div>

          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>
          )}

          <Card className="border-0 shadow-sm">
            <Card.Body>
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ปีการศึกษา</th>
                      <th>ภาคเรียน</th>
                      <th>ชื่องวด</th>
                      <th>ครบกำหนดชำระ</th>
                      <th className="text-center">รายการราคา</th>
                      <th className="text-center">บิลที่ออก</th>
                      <th className="text-center">สถานะ</th>
                      <th className="text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {terms.length === 0 ? (
                      <tr><td colSpan={8} className="text-center text-muted py-4">ยังไม่มีงวดเรียกเก็บ</td></tr>
                    ) : (
                      terms.map((term) => (
                        <tr key={term.term_id}>
                          <td>{term.year_year}</td>
                          <td>ภาค {term.semester}</td>
                          <td>{term.term_name || '-'}</td>
                          <td>{term.due_date ? new Date(term.due_date).toLocaleDateString('th-TH') : '-'}</td>
                          <td className="text-center">{term.plan_count}</td>
                          <td className="text-center">{term.invoice_count}</td>
                          <td className="text-center">
                            {term.term_status === 'open'
                              ? <Badge bg="success">เปิด</Badge>
                              : <Badge bg="secondary">ปิด</Badge>}
                          </td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(term)}>
                              <i className="ri-pencil-line" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => navigate('/fee/plans', { state: { termId: term.term_id } })}
                            >
                              <i className="ri-table-line me-1" />ตั้งราคา
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </main>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'แก้ไขงวด' : 'เปิดงวดเรียกเก็บใหม่'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label>ปีการศึกษา</Form.Label>
              <Form.Select
                value={form.tb_studyyear_year_id}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, tb_studyyear_year_id: e.target.value })}
              >
                <option value="">เลือกปี</option>
                {years.map((y) => (
                  <option key={y.year_id} value={y.year_id}>{y.year_year}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label>ภาคเรียน</Form.Label>
              <Form.Select
                value={form.semester}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, semester: e.target.value })}
              >
                <option value="1">ภาคเรียนที่ 1</option>
                <option value="2">ภาคเรียนที่ 2</option>
              </Form.Select>
            </Col>
            <Col xs={12}>
              <Form.Label>ชื่องวด (ไม่บังคับ)</Form.Label>
              <Form.Control
                value={form.term_name}
                onChange={(e) => setForm({ ...form, term_name: e.target.value })}
                placeholder="เช่น ค่าเทอม 1/2568"
              />
            </Col>
            <Col xs={6}>
              <Form.Label>ครบกำหนดชำระ</Form.Label>
              <Form.Control
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </Col>
            <Col xs={6}>
              <Form.Label>สถานะ</Form.Label>
              <Form.Select
                value={form.term_status}
                onChange={(e) => setForm({ ...form, term_status: e.target.value })}
              >
                <option value="open">เปิด</option>
                <option value="closed">ปิด</option>
              </Form.Select>
            </Col>
          </Row>
          {editing && (
            <small className="text-muted d-block mt-2">
              * เปลี่ยนปี/ภาคเรียนไม่ได้หลังเปิดงวดแล้ว
            </small>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default FeeTerms;
